import type { ShopProduct } from './shopify.types';

const CDN = 'https://cdn.shopify.com/s/files/1/0915/0159/2915/files';

/**
 * Miroir local du catalogue réel : mêmes handles, mêmes titres, prix figés.
 * Sert au développement et aux tests, qui doivent rester déterministes même
 * quand le stock bouge côté Shopify.
 *
 * Les blocs `details` sont des **valeurs de démonstration**, relevées sur le
 * vrai catalogue le 2026-08-01 puis figées et complétées quand un metafield
 * n'était pas renseigné. Elles servent à donner de la matière à la démo hors
 * ligne et aux tests, elles ne font pas autorité : dès que le Storefront est
 * configuré, c'est Shopify qui fait foi. Notes et nombres d'avis bougent tous
 * les jours en production, ceux d'ici sont donc volontairement immobiles.
 *
 * Deux absences sont conservées telles quelles parce qu'elles sont réelles et
 * utiles à tester : la corne de buffle n'a encore aucun avis, et `poidsMinimumKg`
 * ne concerne que les mastications les plus dures.
 */
export const PRODUCT_FIXTURES: ShopProduct[] = [
  {
    id: 'fixture-pack-debutant',
    handle: 'pack-debutant',
    title: 'Pack Découverte - 100% naturel',
    description: 'Le carton d’essai. Il ne reste jamais fermé très longtemps.',
    imageUrl: `${CDN}/6.png?v=1782464022`,
    imageAlt: 'Pack Découverte de mastications naturelles',
    images: [
      { url: `${CDN}/6.png?v=1782464022`, alt: 'Pack Découverte de mastications naturelles' },
      {
        url: `${CDN}/packdecouverte30friandisesbenefices.jpg?v=1758279453`,
        alt: 'Bénéfices du Pack Découverte',
      },
    ],
    price: { amount: '24.90', currencyCode: 'EUR' },
    variantId: 'gid://shopify/ProductVariant/51906382823763',
    availableForSale: true,
    details: {
      tailles: ['Petit', 'Moyen'],
      dureeMastication: ['Courte', 'Moyenne'],
      durete: 3,
      duree: 3,
      digestion: 3,
      matiereGrasse: 2,
      ageMinimumMois: 4,
      pourGloutons: false,
      famille: 'pack',
      proteines: ['boeuf', 'poulet', 'porc'],
      note: 4,
      nombreAvis: 29,
      descriptionCourte:
        '✅ Sans additifs ni conservateurs\n🦴 Favorise l’hygiène dentaire et la mastication\n📦 Idéal pour découvrir ou varier les plaisirs\n🐾 Convient à tous types de chiens (chiot à partir de 6 mois)',
      garantie:
        'Essayez-le pendant 30 jours, on vous le rembourse si vous n’êtes pas satisfait !',
      contenu:
        '🐂 Oreille de bœuf avec poils – Naturelle, croustillante (x1)\n🦴 Sabot de veau – Longue mastication (x1)\n🐖 Groin de porc – Tendre et parfumé (x2)',
      pourQuelChien:
        'Le Pack Découverte convient à tout type de chien à partir de 6 mois, du petit gabarit au grand.',
      conseils:
        'Offrez simplement une friandise de temps en temps, en restant à proximité, et retirez le morceau dès qu’il devient assez petit pour être avalé entier.',
    },
    // Le vrai pack propose deux formats et un abonnement : la démo hors ligne
    // doit montrer le même bloc d'achat que la boutique.
    highlights: [
      '✅ Sans additifs ni conservateurs',
      '🦴 Favorise l’hygiène dentaire et la mastication',
      '🎁 2 peaux de bœuf avec poils offertes',
      '📦 Idéal pour découvrir ou varier les plaisirs',
      '🐾 Convient à tous types de chiens (chiot à partir de 6 mois)',
    ],
    variants: [
      {
        id: 'gid://shopify/ProductVariant/51906382823763',
        title: 'Pack Découverte (30 friandises)',
        price: { amount: '24.90', currencyCode: 'EUR' },
        availableForSale: true,
      },
      {
        id: 'gid://shopify/ProductVariant/51906382856531',
        title: 'Pack géant (55 friandises)',
        price: { amount: '44.90', currencyCode: 'EUR' },
        compareAtPrice: { amount: '49.90', currencyCode: 'EUR' },
        availableForSale: true,
      },
    ],
    sellingPlan: {
      id: 'gid://shopify/SellingPlan/691847231',
      name: 'Abonnez-vous et économisez 10 %',
      cadence: 'Livré toutes les 6 semaines',
    },
  },
  {
    id: 'fixture-filet-de-poulet',
    handle: 'filet-de-poulet',
    title: 'Filet de Poulet',
    description: 'Séché, léger, et sujet à disparition inexpliquée.',
    imageUrl: `${CDN}/44.jpg?v=1775725814`,
    imageAlt: 'Filet de poulet séché pour chien',
    images: [{ url: `${CDN}/44.jpg?v=1775725814`, alt: 'Filet de poulet séché pour chien' }],
    price: { amount: '6.90', currencyCode: 'EUR' },
    variantId: 'gid://shopify/ProductVariant/50233871401299',
    availableForSale: true,
    details: {
      tailles: ['Petit', 'Moyen', 'Grand'],
      dureeMastication: ['Courte'],
      durete: 1,
      duree: 1,
      digestion: 4,
      matiereGrasse: 3,
      ageMinimumMois: 2,
      pourGloutons: false,
      famille: 'lamelle',
      proteines: ['poulet'],
      note: 5,
      nombreAvis: 2,
      descriptionCourte:
        '🍗 Filet de poulet naturel\n❌ Sans additif\n✋ Sans sel\n🐕 Pour tous les chiens et chiots',
    },
  },
  {
    id: 'fixture-sprats',
    handle: 'sprats-petits-poissons',
    title: 'Sprats (petits poissons séchés pour chiens et chiots)',
    description: 'L’odeur arrive dans la pièce avant le sachet.',
    imageUrl: `${CDN}/spratspoissonspourchien.jpg?v=1741417635`,
    imageAlt: 'Sachet de sprats séchés',
    images: [
      { url: `${CDN}/spratspoissonspourchien.jpg?v=1741417635`, alt: 'Sachet de sprats séchés' },
    ],
    price: { amount: '9.90', currencyCode: 'EUR' },
    variantId: 'gid://shopify/ProductVariant/50260922499411',
    availableForSale: true,
    details: {
      tailles: ['Petit', 'Moyen', 'Grand'],
      dureeMastication: ['Courte'],
      durete: 1,
      duree: 1,
      digestion: 5,
      matiereGrasse: 2,
      ageMinimumMois: 2,
      pourGloutons: false,
      famille: 'poisson_sec',
      proteines: ['poisson'],
      note: 5,
      nombreAvis: 9,
      descriptionCourte:
        '100% Gloup !\nFriandise de poisson 100% naturelle et adaptée aux chiots. Hypoallergénique et riche en vitamines B2, B3, B5, B9, B12.',
    },
  },
  {
    id: 'fixture-bois-de-cerf',
    handle: 'bois-de-cerf-entier',
    title: 'Bois de cerf entier',
    description: 'Dure des semaines. Sauf accident, et il y a toujours un accident.',
    imageUrl: `${CDN}/50.jpg?v=1775725073`,
    imageAlt: 'Bois de cerf entier à mâcher',
    images: [{ url: `${CDN}/50.jpg?v=1775725073`, alt: 'Bois de cerf entier à mâcher' }],
    price: { amount: '10.90', currencyCode: 'EUR' },
    variantId: 'gid://shopify/ProductVariant/50241340080467',
    availableForSale: true,
    details: {
      tailles: ['Petit', 'Moyen', 'Grand'],
      dureeMastication: ['Indestructible'],
      durete: 5,
      duree: 5,
      digestion: 2,
      matiereGrasse: 1,
      ageMinimumMois: 12,
      poidsMinimumKg: 5,
      pourGloutons: true,
      famille: 'bois',
      proteines: ['cerf'],
      note: 5,
      nombreAvis: 1,
      descriptionCourte:
        'Le bois de cerf est une friandise de mastication 100% naturelle et stimulante pour votre chien.\nIdéale pour la mastication longue durée.',
    },
  },
  {
    id: 'fixture-peau-de-boeuf',
    handle: 'peau-de-boeuf-pour-chien-12-15-cm',
    title: 'Peau de boeuf pour chien 12-15 cm',
    description: 'Principal suspect dans l’affaire du bocal vide.',
    imageUrl: `${CDN}/52.jpg?v=1775727204`,
    imageAlt: 'Peau de bœuf séchée',
    images: [
      { url: `${CDN}/52.jpg?v=1775727204`, alt: 'Peau de bœuf séchée' },
      {
        url: `${CDN}/PEAUDEBOEUFCARACTERISTIQUES.jpg?v=1775727204`,
        alt: 'Caractéristiques de la peau de bœuf',
      },
    ],
    price: { amount: '5.90', currencyCode: 'EUR' },
    variantId: 'gid://shopify/ProductVariant/50220929646931',
    availableForSale: true,
    details: {
      tailles: ['Petit', 'Moyen', 'Grand'],
      dureeMastication: ['Longue'],
      durete: 3,
      duree: 4,
      digestion: 3,
      matiereGrasse: 2,
      ageMinimumMois: 6,
      pourGloutons: false,
      famille: 'peau',
      proteines: ['boeuf'],
      note: 5,
      nombreAvis: 6,
      descriptionCourte:
        '🦷 Hygiène dentaire : réduit la plaque et le tartre.\n⏳ Plaisir prolongé : longue durée de mastication.\n💪 Riche en protéines.\n🌿 100% naturel, sans additifs ni conservateurs.',
    },
  },
  {
    id: 'fixture-corne-de-buffle',
    handle: 'corne-de-buffle',
    title: 'Corne de buffle',
    description: 'Increvable. Le chien tente quand même.',
    imageUrl: `${CDN}/56.jpg?v=1775725004`,
    imageAlt: 'Corne de buffle à mâcher',
    images: [{ url: `${CDN}/56.jpg?v=1775725004`, alt: 'Corne de buffle à mâcher' }],
    price: { amount: '10.90', currencyCode: 'EUR' },
    variantId: 'gid://shopify/ProductVariant/50241347748179',
    availableForSale: true,
    // Aucun avis sur ce produit dans le vrai catalogue : le cas « pas encore
    // noté » est donc réel, l'affichage doit savoir ne rien montrer.
    details: {
      tailles: ['Moyen', 'Grand'],
      dureeMastication: ['Indestructible'],
      durete: 5,
      duree: 5,
      digestion: 2,
      matiereGrasse: 1,
      ageMinimumMois: 12,
      poidsMinimumKg: 10,
      pourGloutons: true,
      famille: 'corne',
      proteines: ['boeuf'],
      descriptionCourte:
        '🍃 100% Naturelle\n🦷 Hygiène dentaire optimale\n💪 Robuste et résistante\n🚫 Sans conservateurs\n🕓 Mastication prolongée',
    },
  },
  {
    id: 'fixture-oreilles-de-porc',
    handle: 'oreilles-de-porc',
    title: 'Oreilles de cochon',
    // En rupture dans le vrai catalogue : le cas indisponible est donc réel.
    description: 'Momentanément introuvables. Nous n’accusons personne.',
    imageUrl: `${CDN}/32.jpg?v=1775722577`,
    imageAlt: 'Oreilles de cochon séchées',
    images: [
      { url: `${CDN}/32.jpg?v=1775722577`, alt: 'Oreilles de cochon séchées' },
      {
        url: `${CDN}/CARACTERISTIQUESOREILLESDECOCHON_94b75778-7ab5-4bf5-99d2-fbc35f993c8c.jpg?v=1775722577`,
        alt: 'Caractéristiques des oreilles de cochon',
      },
    ],
    price: { amount: '1.90', currencyCode: 'EUR' },
    variantId: 'gid://shopify/ProductVariant/51808644858195',
    availableForSale: false,
    details: {
      tailles: ['Moyen', 'Grand'],
      dureeMastication: ['Longue'],
      durete: 2,
      duree: 2,
      digestion: 3,
      matiereGrasse: 3,
      ageMinimumMois: 3,
      pourGloutons: false,
      famille: 'oreille',
      proteines: ['porc'],
      note: 4.5,
      nombreAvis: 10,
      descriptionCourte:
        '🐷 100% oreilles de porc séchées\n💪 79% de protéines\n🦷 Nettoie les dents naturellement\n🌿 Sans additifs, sans conservateurs',
    },
  },
  {
    id: 'fixture-ailes-de-poulet',
    handle: 'ailes-de-poulet',
    title: 'Ailes de poulet',
    description: 'Le bruit qu’elles font vaut le prix à lui seul.',
    imageUrl: `${CDN}/30.jpg?v=1775725708`,
    imageAlt: 'Ailes de poulet séchées',
    images: [{ url: `${CDN}/30.jpg?v=1775725708`, alt: 'Ailes de poulet séchées' }],
    price: { amount: '3.90', currencyCode: 'EUR' },
    variantId: 'gid://shopify/ProductVariant/50237704208723',
    availableForSale: true,
    details: {
      tailles: ['Petit', 'Moyen'],
      dureeMastication: ['Courte'],
      durete: 2,
      duree: 1,
      digestion: 4,
      matiereGrasse: 2,
      ageMinimumMois: 4,
      pourGloutons: false,
      famille: 'patte',
      proteines: ['poulet'],
      note: 4,
      nombreAvis: 4,
      descriptionCourte:
        '🍗 Ailes de poulet naturelles\n🌾 Sans additif\n😁 Pour des dents saines\n💰 Prix doux',
    },
  },
  {
    id: 'fixture-cous-de-poulet',
    handle: 'cous-de-poulet',
    title: 'Cous de poulet',
    description: 'Disparaissent en trois secondes. Nous avons chronométré.',
    imageUrl: `${CDN}/42.jpg?v=1775725684`,
    imageAlt: 'Cous de poulet séchés',
    images: [{ url: `${CDN}/42.jpg?v=1775725684`, alt: 'Cous de poulet séchés' }],
    price: { amount: '4.90', currencyCode: 'EUR' },
    variantId: 'gid://shopify/ProductVariant/50237737435475',
    availableForSale: true,
    details: {
      tailles: ['Petit', 'Moyen', 'Grand'],
      dureeMastication: ['Courte'],
      durete: 3,
      duree: 2,
      digestion: 4,
      matiereGrasse: 3,
      ageMinimumMois: 6,
      pourGloutons: false,
      famille: 'cou',
      proteines: ['poulet'],
      note: 5,
      nombreAvis: 1,
      descriptionCourte:
        '🐕 Pour tous les chiens et chiots\n😁 Santé dentaire assurée\n🌱 100% naturel',
    },
  },
  {
    id: 'fixture-lamelles-de-boeuf',
    handle: 'lamelles-de-boeuf',
    title: 'Lamelles de Boeuf',
    description: 'Se coupent, se partagent, se négocient.',
    imageUrl: `${CDN}/25.jpg?v=1775726488`,
    imageAlt: 'Lamelles de bœuf séchées',
    images: [
      { url: `${CDN}/25.jpg?v=1775726488`, alt: 'Lamelles de bœuf séchées' },
      { url: `${CDN}/chienmachelamelleboeuf.jpg?v=1775726488`, alt: 'Chien mâchant une lamelle' },
    ],
    price: { amount: '7.90', currencyCode: 'EUR' },
    variantId: 'gid://shopify/ProductVariant/50233837257043',
    availableForSale: true,
    details: {
      tailles: ['Petit', 'Moyen', 'Grand'],
      dureeMastication: ['Courte'],
      durete: 2,
      duree: 2,
      digestion: 4,
      matiereGrasse: 2,
      ageMinimumMois: 4,
      pourGloutons: false,
      famille: 'lamelle',
      proteines: ['boeuf'],
      note: 5,
      nombreAvis: 3,
      descriptionCourte:
        'Un produit 100% naturel et 100% plaisir à mâcher. Sans céréale, sans additif et sans conservateur.',
    },
  },
  {
    id: 'fixture-tripe-dagneau',
    handle: 'tripe-dagneau',
    title: "Tripes d'agneau",
    description: 'L’odeur est un sujet. Le chien n’est pas d’accord.',
    imageUrl: `${CDN}/7.jpg?v=1775560301`,
    imageAlt: 'Tripes d’agneau séchées',
    images: [{ url: `${CDN}/7.jpg?v=1775560301`, alt: 'Tripes d’agneau séchées' }],
    price: { amount: '8.90', currencyCode: 'EUR' },
    variantId: 'gid://shopify/ProductVariant/52372793622867',
    availableForSale: true,
    details: {
      tailles: ['Petit', 'Moyen', 'Grand'],
      dureeMastication: ['Courte'],
      durete: 2,
      duree: 2,
      digestion: 3,
      matiereGrasse: 2,
      ageMinimumMois: 4,
      pourGloutons: false,
      famille: 'abat',
      proteines: ['agneau'],
      note: 5,
      nombreAvis: 2,
      descriptionCourte:
        '🦴 Friandise 100% agneau séchée à l’air libre, sans additifs ni conservateurs\n💪 Texture ferme et moelleuse, parfaite pour une mastication courte\n🍖 Riche en protéines (80,7%) et faible en graisses (3,9%)',
    },
  },
  {
    id: 'fixture-trachee-de-boeuf-20cm',
    handle: 'trachee-de-boeuf-20cm',
    title: 'Trachée de boeuf 20cm',
    description: 'Vingt centimètres au départ. Rarement à l’arrivée.',
    imageUrl: `${CDN}/13.jpg?v=1775559594`,
    imageAlt: 'Trachée de bœuf de vingt centimètres',
    images: [
      { url: `${CDN}/13.jpg?v=1775559594`, alt: 'Trachée de bœuf de vingt centimètres' },
      { url: `${CDN}/tracheedeboeufchien.jpg?v=1775722286`, alt: 'Trachée de bœuf en situation' },
    ],
    price: { amount: '4.90', currencyCode: 'EUR' },
    variantId: 'gid://shopify/ProductVariant/52454635929939',
    availableForSale: true,
    details: {
      tailles: ['Moyen', 'Grand'],
      dureeMastication: ['Longue'],
      durete: 3,
      duree: 4,
      digestion: 4,
      matiereGrasse: 1,
      ageMinimumMois: 6,
      pourGloutons: false,
      famille: 'trachee',
      proteines: ['boeuf'],
      note: 4.86,
      nombreAvis: 7,
      descriptionCourte:
        '🐶 100% trachée de bœuf pour chiens\n🌾 Friandise naturelle sans additifs\n🦷 Parfaite pour la santé dentaire',
    },
  },
  {
    id: 'fixture-nerf-de-boeuf',
    handle: 'nerf-de-boeuf',
    title: 'Nerf de boeuf 15cm',
    description: 'Increvable, ou presque. Le presque est important.',
    imageUrl: `${CDN}/63.jpg?v=1775559521`,
    imageAlt: 'Nerf de bœuf de quinze centimètres',
    images: [{ url: `${CDN}/63.jpg?v=1775559521`, alt: 'Nerf de bœuf de quinze centimètres' }],
    price: { amount: '5.90', currencyCode: 'EUR' },
    variantId: 'gid://shopify/ProductVariant/52463608004947',
    availableForSale: true,
    details: {
      tailles: ['Petit', 'Moyen', 'Grand'],
      dureeMastication: ['Longue'],
      durete: 4,
      duree: 4,
      digestion: 3,
      matiereGrasse: 1,
      ageMinimumMois: 4,
      pourGloutons: false,
      famille: 'nerf',
      proteines: ['boeuf'],
      note: 5,
      nombreAvis: 9,
      descriptionCourte:
        '🦴 Friandise 100% bœuf séchée à l’air libre, sans additifs ni conservateurs\n💪 Texture dure, parfaite pour une mastication longue\n🍖 Riche en protéines (63,9%) et faible en graisses (3,5%)',
    },
  },
  {
    id: 'fixture-bois-de-daim',
    handle: 'bois-de-daim',
    title: 'Bois de daim',
    description: 'Plus tendre que le cerf. Cela reste très relatif.',
    imageUrl: `${CDN}/59.jpg?v=1775725109`,
    imageAlt: 'Bois de daim à mâcher',
    images: [{ url: `${CDN}/59.jpg?v=1775725109`, alt: 'Bois de daim à mâcher' }],
    price: { amount: '12.90', currencyCode: 'EUR' },
    variantId: 'gid://shopify/ProductVariant/50241310163283',
    availableForSale: true,
    details: {
      tailles: ['Petit', 'Moyen', 'Grand'],
      dureeMastication: ['Indestructible'],
      durete: 5,
      duree: 5,
      digestion: 2,
      matiereGrasse: 1,
      ageMinimumMois: 12,
      poidsMinimumKg: 5,
      pourGloutons: true,
      famille: 'bois',
      proteines: ['cerf'],
      note: 5,
      nombreAvis: 1,
      descriptionCourte:
        '🍃 Naturel et durable\n🦷 Nettoie les dents\n💪 Riche en minéraux\n🚫 Sans additifs\n🕓 Longue mastication',
    },
  },
  {
    id: 'fixture-sabots-de-boeuf-veau',
    handle: 'sabots-de-boeuf-veau',
    title: 'Sabots de boeuf',
    description: 'Se garnissent de pâtée. Personne ne s’en plaint.',
    imageUrl: `${CDN}/60.jpg?v=1775724817`,
    imageAlt: 'Sabots de bœuf séchés',
    images: [{ url: `${CDN}/60.jpg?v=1775724817`, alt: 'Sabots de bœuf séchés' }],
    price: { amount: '4.90', currencyCode: 'EUR' },
    variantId: 'gid://shopify/ProductVariant/50261101445459',
    availableForSale: true,
    details: {
      tailles: ['Petit', 'Moyen', 'Grand'],
      dureeMastication: ['Longue'],
      durete: 4,
      duree: 4,
      digestion: 2,
      matiereGrasse: 1,
      ageMinimumMois: 6,
      pourGloutons: false,
      famille: 'sabot',
      proteines: ['boeuf'],
      note: 4.38,
      nombreAvis: 8,
      descriptionCourte:
        'Prêt pour un bon grignotage !\nLa friandise idéale pour les chiens et les chiots dès 6 mois. 100% naturel, sans additif ni conservateur.',
    },
  },
  {
    id: 'fixture-baton-de-fromage-chien',
    handle: 'baton-de-fromage-chien',
    title: 'Bâton de fromage dur',
    description: 'Le fromage de montagne, version chien. Très sérieux.',
    imageUrl: `${CDN}/fromageamacherchien.jpg?v=1741420651`,
    imageAlt: 'Bâton de fromage dur',
    images: [{ url: `${CDN}/fromageamacherchien.jpg?v=1741420651`, alt: 'Bâton de fromage dur' }],
    price: { amount: '6.90', currencyCode: 'EUR' },
    variantId: 'gid://shopify/ProductVariant/50241322746195',
    availableForSale: true,
    details: {
      tailles: ['Petit', 'Moyen', 'Grand'],
      dureeMastication: ['Longue'],
      durete: 4,
      duree: 4,
      digestion: 4,
      matiereGrasse: 3,
      ageMinimumMois: 4,
      pourGloutons: false,
      famille: 'fromage',
      proteines: ['lait'],
      note: 4,
      nombreAvis: 7,
      descriptionCourte:
        'Il reconnaîtra le bruit du paquet !\nFriandise 100% naturelle, délicieuse et tellement gourmande.',
    },
  },
  {
    id: 'fixture-friandise-de-dressage-boeuf',
    handle: 'friandise-de-dressage-boeuf',
    title: 'Friandise de dressage de Boeuf',
    description: 'Pour les ordres qu’il connaît déjà mais négocie encore.',
    imageUrl: `${CDN}/28_386cf390-fc9c-4251-b875-d0f6c897599a.jpg?v=1775726619`,
    imageAlt: 'Friandises de dressage au bœuf',
    images: [
      {
        url: `${CDN}/28_386cf390-fc9c-4251-b875-d0f6c897599a.jpg?v=1775726619`,
        alt: 'Friandises de dressage au bœuf',
      },
    ],
    price: { amount: '5.90', currencyCode: 'EUR' },
    variantId: 'gid://shopify/ProductVariant/50233768378707',
    availableForSale: true,
    details: {
      tailles: ['Petit', 'Moyen', 'Grand'],
      dureeMastication: ['Courte'],
      durete: 1,
      duree: 1,
      digestion: 4,
      matiereGrasse: 2,
      ageMinimumMois: 3,
      pourGloutons: false,
      famille: 'friandise',
      proteines: ['boeuf'],
      note: 3.67,
      nombreAvis: 3,
      descriptionCourte:
        'Friandise de dressage au goût de bœuf, 100% naturelle\n❌ Sans colorants ni arômes artificiels\n👍 Source unique de protéines',
    },
  },
];

/**
 * Collections servies hors ligne : handle de collection → handles produits.
 *
 * Les appartenances ci-dessous ont été relevées sur le vrai catalogue le
 * 2026-08-01. Elles sont donc fidèles, mais partielles : trois produits du
 * miroir local n'appartiennent à aucune des six collections mises en avant, et
 * les jouets comme les tapis de léchage n'ont aucun équivalent local. Dès que le
 * Storefront est configuré, c'est Shopify qui fait foi.
 */
export const FIXTURE_COLLECTIONS: Record<string, string[]> = {
  'produits-mastication': [
    'bois-de-cerf-entier',
    'corne-de-buffle',
    'oreilles-de-porc',
    'bois-de-daim',
    'sabots-de-boeuf-veau',
    'baton-de-fromage-chien',
  ],
  'friandises-dentaires': [
    'corne-de-buffle',
    'lamelles-de-boeuf',
    'trachee-de-boeuf-20cm',
    'sabots-de-boeuf-veau',
  ],
  'jouets-chiens': [],
  'tapis-de-lechage-chiens': [],
  packs: ['pack-debutant'],
  nouveautes: ['tripe-dagneau', 'nerf-de-boeuf'],
};

/** Correspondance décor ↔ catalogue, par handle (cf. §14). */
export const SCENE_PRODUCTS = {
  alimentation: [
    'pack-debutant',
    'filet-de-poulet',
    'sprats-petits-poissons',
    'ailes-de-poulet',
    'cous-de-poulet',
    'lamelles-de-boeuf',
    'tripe-dagneau',
  ],
  friandises: [
    'bois-de-cerf-entier',
    'peau-de-boeuf-pour-chien-12-15-cm',
    'corne-de-buffle',
    'oreilles-de-porc',
    'trachee-de-boeuf-20cm',
    'nerf-de-boeuf',
    'bois-de-daim',
    'sabots-de-boeuf-veau',
  ],
  comptoir: ['baton-de-fromage-chien', 'friandise-de-dressage-boeuf'],
} as const;
